import React from 'react';
import Rewind10Icon from './icons/Rewind10Icon';
import Forward10Icon from './icons/Forward10Icon';
import AnimatedPlayIcon from './AnimatedPlayIcon';
import { useTheme } from '@/contexts/ThemeContext';

interface PlayerControlsProps {
  isPlaying: boolean;
  isReady: boolean;
  isBuffering?: boolean;
  isConnected?: boolean;
  onTogglePlay: () => void;
  onSkipBackward: () => void;
  onSkipForward: () => void;
  size?: 'base' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  leadingControl?: React.ReactNode;
  trailingControl?: React.ReactNode;
  distributed?: boolean;
  plainPlayButton?: boolean;
  playOnly?: boolean;
}

const PlayerControls: React.FC<PlayerControlsProps> = ({
                                                         isPlaying,
                                                         isReady,
                                                         isBuffering,
                                                         isConnected = false,
                                                         onTogglePlay,
                                                         onSkipBackward,
                                                         onSkipForward,
                                                         size = 'md',
                                                         leadingControl,
                                                         trailingControl,
                                                         distributed = false,
                                                         plainPlayButton = false,
                                                         playOnly = false,
                                                       }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // Determine which icon to show
  const showStopIcon = isBuffering || isConnected;

  const outerButtonSizes = {
    base: 'w-12 h-12',
    xs: 'w-10 h-10',
    sm: 'w-12 h-12',
    md: 'w-14 h-14',
    lg: 'w-16 h-16',
    xl: 'w-18 h-18',
  };

  const space = {
    base: 'space-x-2',
    xs: 'space-x-2',
    sm: 'space-x-3',
    md: 'space-x-6',
    lg: 'space-x-7',
    xl: 'space-x-8',
  }[size];

  const secondaryButtonClass = `flex justify-center items-center aspect-square rounded-full transition-colors cursor-pointer ${outerButtonSizes[size]} ${
    isDarkMode
      ? 'text-gray-200'
      : 'text-gray-800'
  }`;
  const playButtonClass = plainPlayButton
    ? secondaryButtonClass
    : `flex justify-center items-center rounded-full bg-gray-800 text-white transition-colors aspect-square cursor-pointer shadow-md ${outerButtonSizes[size]}`;
  const transportIconSize = plainPlayButton
    ? ({
        base: 'xs',
        xs: 'xs',
        sm: 'sm',
        md: 'md',
        lg: 'md',
        xl: 'lg',
      } as const)[size]
    : size;

  if (playOnly) {
    return (
      <div className="flex justify-center items-center">
        <button
          data-tutorial-id="player-playpause"
          onClick={onTogglePlay}
          aria-label={showStopIcon ? 'Stop conversation' : isPlaying ? 'Pause audio' : 'Play audio'}
          className={secondaryButtonClass}
          disabled={!isReady}
        >
          <AnimatedPlayIcon
            isPlaying={isPlaying}
            showStopIcon={showStopIcon}
            size={size}
            className="scale-150"
          />
        </button>
      </div>
    );
  }

  return (
    <div className={`flex items-center ${distributed ? 'w-full justify-between' : `justify-center ${space}`}`}>
      {leadingControl}

      <button
        onClick={onSkipBackward}
        aria-label="Skip backward 10 seconds"
        className={secondaryButtonClass}
        disabled={!isReady}
      >
        <Rewind10Icon size={transportIconSize} aria-hidden="true" />
      </button>

      <button
        data-tutorial-id="player-playpause"
        onClick={onTogglePlay}
        aria-label={showStopIcon ? "Stop conversation" : isPlaying ? "Pause audio" : "Play audio"}
        className={playButtonClass}
        disabled={!isReady}
      >
        <AnimatedPlayIcon
          isPlaying={isPlaying}
          showStopIcon={showStopIcon}
          size={size}
          className={plainPlayButton ? 'scale-[1.75]' : ''}
        />
      </button>

      <button
        onClick={onSkipForward}
        aria-label="Skip forward 10 seconds"
        className={secondaryButtonClass}
        disabled={!isReady}
      >
        <Forward10Icon size={transportIconSize} aria-hidden="true" />
      </button>

      {trailingControl}
    </div>
  );
};

export default PlayerControls;
